import React, { useState } from 'react';
import styled from 'styled-components';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';

import { ModalContainer, ModalPrimaryHeader } from 'shared/modals';
import { DragHandle } from 'src/svg/drag-handle';
import Button from 'shared/modals/button';
import { useDispensary } from 'src/hooks/use-dispensary';
import { getCategoryLabels } from 'src/dispensary-admin/customize/customize.utils';
import { Categories } from '../categories.types';

type ReorderModalProps = {
  categories: Categories;
  onToggleModal: () => void;
  onSaveOrder: (orderedItems: Categories) => void;
};

export const ReorderModal = ({ categories, onToggleModal, onSaveOrder }: ReorderModalProps): JSX.Element => {
  const dispensary = useDispensary();
  const categoryLabels = getCategoryLabels(dispensary.location?.state);
  const [orderedCategories, setOrderedCategories] = useState(categories);

  const reorder = (list: Categories, startIndex: number, endIndex: number): Categories => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result: DropResult): void => {
    if (!result.destination) {
      return;
    }

    const updatedCategories = reorder(orderedCategories, result.source.index, result.destination.index);
    setOrderedCategories(updatedCategories);
  };

  const saveOrder = (): void => {
    onSaveOrder(orderedCategories);
    onToggleModal();
  };

  return (
    <ModalContainer
      isOpen
      onRequestClose={onToggleModal}
      width='500px'
      maxHeight='80vh'
      alignItems='flex-start'
      padding='60px 0 88px'
      ariaHideApp={false}
      textAlign='left'
      scrollable
      overflow='hidden'
    >
      <ModalPrimaryHeader copy='Customize Category Order' close={onToggleModal} />

      <Content>
        <Description>Drag and drop to reorder how categories will appear on your menu.</Description>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId='categories'>
            {(droppableProvided) => (
              <List {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                {orderedCategories.map(({ category }, index: number) => (
                  <Draggable key={category} draggableId={category} index={index}>
                    {(draggableProvided) => (
                      <ListItem
                        key={category}
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        {categoryLabels?.[category] ?? category}
                        <DragHandle />
                      </ListItem>
                    )}
                  </Draggable>
                ))}

                {droppableProvided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </Content>

      <Footer>
        <Button alignItems='flex-start' mt='0' onClick={saveOrder}>
          Save Category Order
        </Button>
      </Footer>
    </ModalContainer>
  );
};

const Content = styled.div`
  overflow: auto;
  padding: 24px 32px;
  width: 100%;
`;

const Description = styled.div`
  font-size: 13px;
  line-height: ${21 / 13};
  margin-bottom: 16px;
`;

const List = styled.div`
  font-size: 13px;
  line-height: 1;
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
`;

const ListItem = styled.div`
  align-items: center;
  background: #fff;
  border: 1px solid #d3d8de;
  border-radius: 8px;
  cursor: grab;
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  width: 100%;

  & + & {
    margin-top: 8px;
  }
`;

const Footer = styled.div`
  background: #fff;
  box-shadow: 0 -5px 8px 0 #0000000d;
  bottom: 0;
  left: 0;
  padding: 24px 32px;
  position: absolute;
  width: 100%;
`;
